import { selectBackupRetention } from "@classroom/domain";
import { useState } from "react";
import { interfaceLocale, useTranslation } from "../../i18n";
import { MIN_BACKUP_RECOVERY_PASSWORD_LENGTH } from "../localStore/automaticBackupCrypto";
import type { LocalBackupSource } from "../localStore/repository";
import type { useAutomaticBackup } from "./useAutomaticBackup";
import "./automatic-backup.css";

export function backupErrorKey(code: string) {
  if (code === "sync-backup-setup-password-mismatch") return "errorSetupPassword";
  if (code === "sync-backup-recent-login-required") return "errorRecentLogin";
  if (["sync-backup-recovery-invalid", "sync-backup-password-length"].includes(code)) return "errorPassword";
  if (code === "sync-backup-sync-required") return "errorSync";
  if (code === "sync-backup-overdue") return "errorOverdue";
  if (code === "sync-backup-limit") return "errorLimit";
  if (code === "sync-draft-save-required") return "errorDraft";
  if (code === "sync-backup-current-required") return "errorChanged";
  if (code === "sync-drive-quota") return "errorQuota";
  if (["sync-google-reconnect", "sync-drive-unbound", "sync-drive-denied"].includes(code)) return "errorAuth";
  if (["sync-backup-device-required", "sync-device-revoked", "sync-device-signature"].includes(code))
    return "errorDevice";
  if (["sync-backup-format", "sync-backup-protected"].includes(code)) return "errorFormat";
  if (["sync-action-unavailable", "sync-not-released", "sync-provider-unconfigured"].includes(code))
    return "errorService";
  return "errorGeneric";
}
export function backupStatusKey(status: ReturnType<typeof useAutomaticBackup>["status"]) {
  if (!status.ready) return "checking";
  if (status.busy) return "working";
  if (!status.config?.enabled) return "disabled";
  if (!status.unlocked) return "unlockNeeded";
  if (status.error) return "unavailable";
  if (status.progress.pendingSince !== null) return "pending";
  return status.progress.lastCompletedAt ? "saved" : "checking";
}
export function AutomaticBackupPanel({
  controller,
  newDevice,
}: {
  controller: ReturnType<typeof useAutomaticBackup>;
  newDevice: boolean;
}) {
  const { t } = useTranslation("automaticBackup");
  const { status } = controller;
  const [password, setPassword] = useState(""),
    [confirmation, setConfirmation] = useState(""),
    [trusted, setTrusted] = useState(false),
    [formError, setFormError] = useState("");
  const [googleUrl, setGoogleUrl] = useState("");
  const [preview, setPreview] = useState<{ id: string; source: LocalBackupSource } | null>(null);
  const date = (time: number | null) =>
    time === null
      ? t("none")
      : new Intl.DateTimeFormat(interfaceLocale(), {
          timeZone: "Asia/Hong_Kong",
          dateStyle: "medium",
          timeStyle: "short",
        }).format(time);
  const retention = selectBackupRetention(
    status.records.map((r) => r.version),
    Date.now(),
  );
  const submit = async () => {
    setFormError("");
    if (!status.config && password !== confirmation) {
      setFormError(t("mismatch"));
      return;
    }
    try {
      if (status.config) await controller.unlock(password);
      else await controller.initialize(password);
    } finally {
      setPassword("");
      setConfirmation("");
    }
  };
  return (
    <div className="automatic-backup-panel">
      <p role="status">{t(backupStatusKey(status))}</p>
      <p className="automatic-backup-note">{t("leaveHint")}</p>
      {status.error && <p role="alert">{t(backupErrorKey(status.error))}</p>}
      {status.restored && (
        <div role="status">
          <p>{t("restored")}</p>
          <button type="button" onClick={() => location.reload()} disabled={status.busy || status.recoveryPending}>
            {t("reopen")}
          </button>
        </div>
      )}
      {status.recoveryPending && (
        <button type="button" disabled={status.busy} onClick={() => void controller.recoverConnection()}>
          {t("resumeRecovery")}
        </button>
      )}
      {status.ready && !status.unlocked && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <p>{t(status.config ? "unlockHint" : "passwordHint")}</p>
          <label>
            {t("password")}
            <input
              type="password"
              autoComplete={status.config ? "current-password" : "new-password"}
              value={password}
              minLength={MIN_BACKUP_RECOVERY_PASSWORD_LENGTH}
              maxLength={1024}
              required
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {!status.config && (
            <label>
              {t("confirmPassword")}
              <input
                type="password"
                autoComplete="new-password"
                value={confirmation}
                minLength={MIN_BACKUP_RECOVERY_PASSWORD_LENGTH}
                maxLength={1024}
                required
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </label>
          )}
          <label className="automatic-backup-consent">
            <input type="checkbox" checked={trusted} required onChange={(event) => setTrusted(event.target.checked)} />
            {t("trust")}
          </label>
          <p className="automatic-backup-note">{t("retention")}</p>
          {formError && <p role="alert">{formError}</p>}
          <button type="submit" className="primary-action" disabled={status.busy || !trusted}>
            {t(status.busy ? "working" : status.config ? "unlock" : "setup")}
          </button>
        </form>
      )}
      {status.unlocked && (
        <>
          <div className="automatic-backup-actions">
            <span>
              {t("drive")}: {status.drive?.displayEmail ?? t("none")}
            </span>
            <button
              type="button"
              disabled={status.busy}
              onClick={() =>
                void controller.connect().then((result) => {
                  if (result) setGoogleUrl(result.url);
                })
              }
            >
              {t("connect")}
            </button>
            {googleUrl && (
              <a href={googleUrl} target="_blank" rel="noopener noreferrer">
                {t("openGoogle")}
              </a>
            )}
            <button type="button" disabled={status.busy} onClick={() => void controller.refresh()}>
              {t("refresh")}
            </button>
          </div>
          {status.oauthCandidate && (
            <div>
              <p>{status.oauthCandidate.displayEmail}</p>
              {status.drive && status.drive.subject !== status.oauthCandidate.subject ? (
                <p role="alert">{t("wrongDrive")}</p>
              ) : (
                <button
                  type="button"
                  disabled={status.busy}
                  onClick={() =>
                    void controller.confirmDrive(status.oauthCandidate!.subject).then(() => setGoogleUrl(""))
                  }
                >
                  {t("confirmDrive")}
                </button>
              )}
            </div>
          )}
          {status.drive && (
            <div className="automatic-backup-actions">
              {!status.recoveryVerified || !status.records.length ? (
                <button type="button" disabled={status.busy} onClick={() => void controller.resume()}>
                  {t("resume")}
                </button>
              ) : (
                <>
                  <button type="button" disabled={status.busy} onClick={() => void controller.backupNow()}>
                    {t("now")}
                  </button>
                  <button
                    type="button"
                    disabled={status.busy}
                    onClick={() => void controller.toggle(!status.config?.enabled)}
                  >
                    {t(status.config?.enabled ? "disable" : "enable")}
                  </button>
                </>
              )}
            </div>
          )}
          <dl className="automatic-backup-times">
            <div>
              <dt>{t("lastSuccess")}</dt>
              <dd>{date(status.progress.lastCompletedAt)}</dd>
            </div>
            <div>
              <dt>{t("lastCheck")}</dt>
              <dd>{date(status.progress.lastCheckedAt)}</dd>
            </div>
            {status.progress.pendingSince !== null && (
              <div>
                <dt>{t("pendingSince")}</dt>
                <dd>{date(status.progress.pendingSince)}</dd>
              </div>
            )}
          </dl>
          {!navigator.onLine && <p>{t("connectionRequired")}</p>}
          {status.records.length ? (
            <ul className="automatic-backup-list" aria-label={t("title")}>
              {status.records.map(({ version }) => (
                <li key={version.id}>
                  <div>
                    <strong>{date(version.dataAt)}</strong>
                    <span>
                      {t(
                        version.protected
                          ? "protected"
                          : retention.recent[0] === version.id
                            ? "latest"
                            : retention.recent.includes(version.id)
                              ? "recent"
                              : retention.daily.includes(version.id)
                                ? "daily"
                                : "branch",
                      )}
                    </span>
                  </div>
                  <div className="automatic-backup-actions">
                    <button
                      type="button"
                      disabled={status.busy || !navigator.onLine}
                      onClick={() =>
                        void controller.inspect(version.id).then((value) => {
                          if (value) setPreview({ id: version.id, source: value.source });
                        })
                      }
                    >
                      {t("inspect")}
                    </button>
                    <button
                      type="button"
                      disabled={status.busy || !navigator.onLine || status.restored}
                      onClick={() => {
                        if (
                          window.confirm(
                            t(newDevice ? "recoveryConfirm" : "restoreConfirm", { time: date(version.dataAt) }),
                          )
                        )
                          void controller.restore(version.id);
                      }}
                    >
                      {t("restore")}
                    </button>
                  </div>
                  <details>
                    <summary>{t("details")}</summary>
                    <dl>
                      <dt>{t("covered")}</dt>
                      <dd>{date(version.dataAt)}</dd>
                      <dt>{t("completed")}</dt>
                      <dd>{date(version.completedAt)}</dd>
                      <dt>{t("source")}</dt>
                      <dd>{version.deviceId.slice(0, 8)}</dd>
                    </dl>
                  </details>
                  {preview?.id === version.id && (
                    <div>
                      <p>{t("readOnly")}</p>
                      <p>
                        {t("records")}: {preview.source.snapshot.records.length}
                      </p>
                      <p>
                        {t("classes")}:{" "}
                        {preview.source.snapshot.records
                          .filter((row) => row.collection === "classes")
                          .map((row) => String(row.data.name ?? ""))
                          .join("、")}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>{t("empty")}</p>
          )}
          <p className="automatic-backup-note">{t("retention")}</p>
        </>
      )}
    </div>
  );
}
